import {
  Item,
  Button,
  Checkbox,
  Icon,
  TextArea,
  Segment,
} from 'semantic-ui-react';
import React, { useCallback, useEffect, useState } from 'react';
import { ReservationPolicy as  ReservationPolicyAPI} from '../../api/services/next';
import { onError, onSuccess } from '../../components/shared/messages';
import { useTextResources } from '../../language';
import NumericInput from '../../components/shared/NumericInput';
import SideBar from '../../components/shared/SideBar';
import TextInput from '../../components/shared/TextInput';
import { useLoader } from '../../components/shared/utils';
import { MenuActions } from '../../components/shared/constants';
import CancellationAndDepositPoliciesPopup from '../../components/CancellationAndDepositPoliciesPopup';
import ReservationPolicyUpdatePopup from '../../components/ReservationPolicyUpdatePopup';
import uniqid from 'uniqid';

const policyPropertiesEnum = {
  Name: 'Name',
  Description: 'Description',
  Priority: 'Priority',
  IsActive: 'IsActive',
};

const ReservationPolicy = () => {
  const text = useTextResources();
  const [reservationPolicies, setReservationPolicies] = useState([]);
  const [filteredReservationPolicies, setFilteredReservationPolicies] =
    useState([]);
  const [
    showCancellationAndDepositPoliciesPopup,
    setShowCancellationAndDepositPoliciesPopup,
  ] = useState(false);
  const [chosenReservationPolicy, setChosenReservationPolicy] = useState({});
  const [
    reservationPolicyUpdatePopupVisible,
    setReservationPolicyUpdatePopupVisible,
  ] = useState(true);
  const loader = useLoader();

  useEffect(() => {
    window.optima.commands.unsubscribe(MenuActions.toggle);
    window.optima.commands.subscribe(MenuActions.toggle, () => {
      setReservationPolicyUpdatePopupVisible((prev) => !prev);
    });
  }, [reservationPolicyUpdatePopupVisible]);

  useEffect(() => {
    (async () => {
      try {
        loader(true);
        let result = await ReservationPolicyAPI.GetReservationPolicies();
        result = result.sort((a, b) => b.Priority - a.Priority);
        setReservationPolicies(result);
        setFilteredReservationPolicies(result);
      } catch (error) {
        onError(error);
      } finally {
        loader(false);
      }
    })();
  }, []);

  useEffect(() => {
    setFilteredReservationPolicies((prev) =>
      reservationPolicies.filter((i) =>
        prev.find((item) => item.Id == i.Id || (i.Id == 0 && i.IsActive))
      )
    );
  }, [reservationPolicies]);

  useEffect(() => {
    if (window?.optima?.commands) {
      window.optima.commands.unSubscribeAll(MenuActions.save);
      window.optima.commands.subscribe(MenuActions.save, save);
    }
  }, [reservationPolicies]);

  const save = () => {
    (async () => {
      try {
        loader(true);
        if (reservationPolicies?.length) {
          let result = await ReservationPolicyAPI.AddReservationPolicies(
            reservationPolicies
          );
          result = result.sort((a, b) => b.Priority - a.Priority);
          setReservationPolicies(result);
          setFilteredReservationPolicies(result);
          onSuccess('PoliciesHaveBeenSavedSuccessfully');
        }
      } catch (error) {
        onError(error);
      } finally {
        loader(false);
      }
    })();
  };

  const onAddNewPolicy = () => {
    const newPolicy = {
      Id: 0,
      InternalId: uniqid('newPolicy-'),
      Name: '',
      Description: '',
      IsActive: true,
      Priority: null,
    };
    setReservationPolicies((prev) => [newPolicy, ...prev]);
  };

  const onUpdatePolicy = ({ value, property, currentPolicy }) => {
    setReservationPolicies((prev) =>
      prev.map((prevPolicy) =>
        prevPolicy.Id == currentPolicy.Id && //Policy that was fetched from the server
        prevPolicy.InternalId == currentPolicy.InternalId
          ? //is a new policy - if old policy will be undefined == undefined is true
            { ...currentPolicy, [property]: value }
          : prevPolicy
      )
    );
  };

  const onCheckBoxChange = (e, data) => {
    if (data.checked)
      setFilteredReservationPolicies((prev) =>
        prev.filter((rp) => rp.IsActive)
      );
    else setFilteredReservationPolicies(reservationPolicies);
  };

  const choosePolicyAndOpenModal = (chosenPolicy) => {
    setChosenReservationPolicy(chosenPolicy);
    setShowCancellationAndDepositPoliciesPopup(true);
  };

  const closeModalAndCleanChosenPolicy = useCallback(
    ({
      cancellations = [],
      deposits = [],
      reservationPolicyId = null,
    } = {}) => {
      setChosenReservationPolicy({});
      setReservationPolicies((prev) =>
        prev.map((r) =>
          r.Id == reservationPolicyId
            ? {
                ...r,
                CancellationPolicies: cancellations,
                DepositPolicies: deposits,
              }
            : r
        )
      );
      setShowCancellationAndDepositPoliciesPopup(false);
    },
    []
  );

  return (
    <>
      <SideBar
        items={[{
          id: 1,
          cb: () => {
            window['PolicyController']('open', {
              HotelId: window.optima.user.CurrentHotel.Id,
            });
          },
          iconName: 'calendar times outline',
          toolTip: 'UpdatePolicy',
        }]}
        visible={true}
        onHide={() => setReservationPolicyUpdatePopupVisible(false)}
      >
        <Segment secondary>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              positive
              icon='plus'
              content={text('AddNewPolicy')}
              onClick={onAddNewPolicy}
            />
            <Checkbox
              label={text('ShowOnlyActive')}
              onChange={onCheckBoxChange}
              defaultChecked={false}
            />
          </div>
        </Segment>
        <Segment>
          <Item.Group divided>
            {filteredReservationPolicies.length ? (
              filteredReservationPolicies.map((currentPolicy) => (
                <Item key={currentPolicy.Id}>
                  <Item.Content>
                    <Item.Meta style={{ display: 'flex' }}>
                      <TextInput
                        style={{ flexGrow: '2' }}
                        transparent
                        fluid
                        size='large'
                        placeholder={text(policyPropertiesEnum.Name)}
                        onChange={(value) =>
                          onUpdatePolicy({
                            value,
                            currentPolicy,
                            property: policyPropertiesEnum.Name,
                          })
                        }
                        value={currentPolicy.Name}
                      />

                      <NumericInput
                        size='mini'
                        placeholder={text(policyPropertiesEnum.Priority)}
                        min={0}
                        max={5}
                        digits={0}
                        onChange={(value) =>
                          onUpdatePolicy({
                            value,
                            currentPolicy,
                            property: policyPropertiesEnum.Priority,
                          })
                        }
                        value={currentPolicy.Priority}
                      />
                    </Item.Meta>
                    <Item.Description>
                      <TextArea
                        rows={2}
                        placeholder={text(policyPropertiesEnum.Description)}
                        maxLength='500'
                        style={{
                          minWidth: '-webkit-fill-available',
                          maxWidth: 'fit-content',
                          resize: 'none',
                          border: 'none',
                          outline: 'none',
                          fontSize: 'initial',
                        }}
                        onChange={(e, data) =>
                          onUpdatePolicy({
                            value: data.value,
                            currentPolicy,
                            property: policyPropertiesEnum.Description,
                          })
                        }
                        value={currentPolicy.Description}
                      />
                    </Item.Description>
                    <Item.Extra>
                      <Checkbox
                        label={text(
                          currentPolicy.IsActive ? 'Active' : 'NotActive'
                        )}
                        checked={currentPolicy.IsActive}
                        onChange={(e, data) =>
                          onUpdatePolicy({
                            value: data.checked,
                            currentPolicy,
                            property: policyPropertiesEnum.IsActive,
                          })
                        }
                      />
                      <Button
                        icon
                        size='mini'
                        labelPosition='right'
                        floated='right'
                        onClick={() => choosePolicyAndOpenModal(currentPolicy)}
                        disabled={!currentPolicy.Id}
                      >
                        <Icon name='pencil' />
                        {text('SetPolicy')}
                      </Button>
                    </Item.Extra>
                  </Item.Content>
                </Item>
              ))
            ) : (
              <div>{text('NoPoliciesToShow')}</div>
            )}
          </Item.Group>
        </Segment>
      </SideBar>

      {Object.keys(chosenReservationPolicy).length ? (
        <CancellationAndDepositPoliciesPopup
          startOpen={showCancellationAndDepositPoliciesPopup}
          onClose={closeModalAndCleanChosenPolicy}
          reservationPolicy={chosenReservationPolicy}
        />
      ) : null}
      <ReservationPolicyUpdatePopup controller='PolicyController' />
    </>
  );
};

export default ReservationPolicy;
